import { Component, ErrorHandler, inject, Injectable, Injector, signal } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * What browsers say when a lazy chunk is gone: after a deploy, a page loaded
 * from the old build asks for chunk files the new build no longer has.
 * Chrome, Firefox and Safari respectively.
 */
const STALE_CHUNK =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

const isStaleChunkError = (error: unknown) =>
  STALE_CHUNK.test(
    error instanceof Error
      ? error.message
      : String((error as { message?: unknown })?.message ?? error),
  );

/** Notices when Friendlai was updated under an open page, and offers a reload. */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  readonly stale = signal(false);
  /** Where the visitor was headed when a lazy page failed to load, so the reload lands there. */
  private target: string | null = null;

  constructor() {
    inject(Router)
      .events.pipe(filter((e) => e instanceof NavigationError))
      .subscribe((e: NavigationError) => {
        if (isStaleChunkError(e.error)) {
          this.target = e.url;
          this.stale.set(true);
        }
      });
  }

  reload() {
    if (this.target) location.assign(this.target);
    else location.reload();
  }
}

/** Every error Angular sees passes here, including failed lazy routes and unhandled rejections. */
@Injectable()
export class AppErrorHandler extends ErrorHandler {
  // Looked up when needed: the service needs the Router, which may not exist yet when this does.
  private injector = inject(Injector);

  override handleError(error: unknown) {
    if (isStaleChunkError(error)) this.injector.get(AppUpdateService).stale.set(true);
    super.handleError(error);
  }
}

@Component({
  selector: 'app-update-banner',
  styles: `
    .banner {
      position: fixed;
      left: 1rem;
      right: 1rem;
      bottom: 1rem;
      z-index: 101;
      max-width: 560px;
      margin: 0 auto;
      padding: 1rem 1.25rem;
      display: flex;
      gap: 1rem;
      align-items: center;
      border: 1px solid var(--fai-color-border);
      border-radius: 12px;
      background: var(--fai-color-surface);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }
    p {
      flex: 1;
      margin: 0;
    }
    button {
      flex: 0 0 auto;
    }
  `,
  template: `
    @if (update.stale()) {
      <div class="banner" role="alert">
        <p>Friendlai has been updated. Reload to keep using it.</p>
        <button (click)="update.reload()">Reload</button>
      </div>
    }
  `,
})
export class UpdateBanner {
  update = inject(AppUpdateService);
}
